<script setup lang="ts">
import { ClipboardPaste, Loader2 } from 'lucide-vue-next'
import PasteUrlModal from './PasteUrlModal.vue'
import { isValidDomain as checkDomain, sanitizeDomain } from '../utils/domain'

const props = defineProps<{
  modelValue: string
  isLoading?: boolean
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'next'): void
  (e: 'cancel'): void
}>()

const { t } = useI18n()

const domain = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', sanitizeDomain(value))
})

const isValidDomain = computed(() => checkDomain(domain.value))

const isPasteModalOpen = ref(false)

const applyPastedDomain = (value: string) => {
  domain.value = value
}

const handleNext = () => {
  if (isValidDomain.value) {
    emit('next')
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <h3 class="text-lg font-semibold text-foreground">
        {{ t('pages.domain.config.steps.domain.title') }}
      </h3>
      <p class="text-sm text-muted-foreground mt-1">
        {{ t('pages.domain.config.steps.domain.description') }}
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <AppFormInput
        id="domainName"
        v-model="domain"
        :label="t('pages.domain.config.steps.domain.inputLabel')"
        type="text"
        :placeholder="t('pages.domain.config.steps.domain.inputPlaceholder')"
        name="domainName"
        :disabled="disabled"
        required
      >
        <template #icon-right>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            class="-mx-3 h-full rounded-l-none text-foreground-secondary"
            :disabled="disabled"
            @click="isPasteModalOpen = true"
          >
            <ClipboardPaste />
            {{ t('pages.domain.config.steps.domain.pasteButton') }}
          </Button>
        </template>
      </AppFormInput>
      <div class="flex items-center gap-2">
        <BaseIcon name="IconoirInfoCircle" class="w-4 h-4 text-muted-foreground" />
        <p class="text-xs text-muted-foreground">{{ t('pages.domain.config.steps.domain.tip') }}</p>
      </div>
    </div>

    <PasteUrlModal :is-open="isPasteModalOpen" @close="isPasteModalOpen = false" @apply="applyPastedDomain" />

    <BaseTip severity="warn" :text="t('pages.domain.config.steps.domain.immutableWarning')" />

    <div class="flex gap-3">
      <Button variant="outline" @click="$emit('cancel')">
        {{ t('pages.domain.config.buttons.back') }}
      </Button>
      <Button variant="default-primary" :disabled="!isValidDomain || isLoading" @click="handleNext">
        <Loader2 v-if="isLoading" class="h-4 w-4 animate-spin" />
        {{ t('pages.domain.config.steps.domain.submitButton') }}
      </Button>
    </div>
  </div>
</template>
